import { useState } from "react";
import {
  labAnalyteName,
  type LabAnalyteMapping,
  type LabAnalyteMappingList,
  type RerunLabPortalenQuarantineResult,
} from "@care-by-ella/contracts";
import { UnauthorizedError } from "@/lib/clinician-api";
import { errorText } from "@/lib/error-text";
import {
  convertPreview,
  mappingScopeText,
  parseFactor,
  resolutionReasonError,
  unitNeedsFactor,
} from "@/lib/labportalen";
import {
  createAnalyteMapping,
  deactivateAnalyteMapping,
  rerunLabPortalenQuarantine,
} from "@/lib/labportalen-api";

// TES-1263 — "Koppla till analys": a doctor or admin teaches the sweep what a
// lab's analysis code means, lists the mappings, deactivates one, and re-runs
// the open reports the mappings can now read. Every action takes a reason,
// which the API audits. Never automatic. Every Swedish string here is
// PROPOSED copy awaiting Emelie.

function failText(e: unknown): string {
  return errorText(e instanceof Error ? e.message : String(e));
}

function fmtDate(iso: string | null): string {
  if (!iso) return "–";
  return new Date(iso).toLocaleDateString("sv-SE", {
    timeZone: "Europe/Stockholm",
  });
}

function fmtNumber(n: number): string {
  return String(n).replace(".", ",");
}

function ReasonInput({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
}) {
  return (
    <label className="flex flex-col gap-xs text-sm">
      {label}
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={500}
        rows={2}
        required
        className="rounded-md border border-surfaceMuted bg-surface p-sm text-sm"
      />
      <span className="text-xs text-textSecondary">
        Sparas i åtkomstloggen.
      </span>
    </label>
  );
}

const inputClass =
  "px-sm py-xs rounded-md border border-surfaceMuted bg-surface text-sm text-textPrimary";

export function MappingForm({
  initial,
  panel,
  onDone,
  onCancel,
  onUnauthorized,
}: {
  initial: { testMethodCode: string; anaName: string; reportedUnit: string };
  panel: LabAnalyteMappingList["panel"];
  onDone: () => void;
  onCancel: () => void;
  onUnauthorized: () => void;
}) {
  const [testMethodCode, setCode] = useState(initial.testMethodCode);
  const [anaName, setName] = useState(initial.anaName);
  const [reportedUnit, setUnit] = useState(initial.reportedUnit);
  const [labCode, setLab] = useState("");
  const [analyteKey, setAnalyteKey] = useState("");
  const [factorText, setFactorText] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const target = panel.find((p) => p.analyteKey === analyteKey) ?? null;
  const needsFactor =
    target !== null && unitNeedsFactor(reportedUnit, target.unit);
  const factor = parseFactor(factorText);
  const reasonError = resolutionReasonError(reason);
  const ready =
    (testMethodCode.trim() !== "" || anaName.trim() !== "") &&
    reportedUnit.trim() !== "" &&
    target !== null &&
    (needsFactor ? typeof factor === "number" : true) &&
    !reasonError;

  async function submit() {
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    try {
      await createAnalyteMapping({
        testMethodCode: testMethodCode.trim() || null,
        anaName: anaName.trim() || null,
        labCode: labCode.trim() || null,
        reportedUnit: reportedUnit.trim(),
        analyteKey,
        conversionFactor: needsFactor ? (factor as number) : null,
        reason: reason.trim(),
      });
      onDone();
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized();
        return;
      }
      setError(failText(e));
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      className="flex flex-col gap-sm max-w-2xl border border-surfaceMuted rounded-md p-md bg-surface"
    >
      <h3 className="text-sm font-medium">Koppla till analys</h3>
      <div className="grid grid-cols-2 gap-sm">
        <label className="flex flex-col gap-xs text-xs text-textSecondary">
          Kod
          <input
            value={testMethodCode}
            onChange={(e) => setCode(e.target.value)}
            className={`${inputClass} font-mono`}
          />
        </label>
        <label className="flex flex-col gap-xs text-xs text-textSecondary">
          Namn
          <input
            value={anaName}
            onChange={(e) => setName(e.target.value)}
            className={inputClass}
          />
        </label>
        <label className="flex flex-col gap-xs text-xs text-textSecondary">
          Enhet
          <input
            value={reportedUnit}
            onChange={(e) => setUnit(e.target.value)}
            required
            className={inputClass}
          />
        </label>
        <label className="flex flex-col gap-xs text-xs text-textSecondary">
          Labbets kod (valfritt)
          <input
            value={labCode}
            onChange={(e) => setLab(e.target.value)}
            className={`${inputClass} font-mono`}
          />
        </label>
      </div>
      <label className="flex flex-col gap-xs text-xs text-textSecondary">
        Analys i panelen
        <select
          value={analyteKey}
          onChange={(e) => setAnalyteKey(e.target.value)}
          required
          className={inputClass}
        >
          <option value="">Välj analys</option>
          {panel.map((p) => (
            <option key={p.analyteKey} value={p.analyteKey}>
              {labAnalyteName(p.analyteKey)} ({p.unit})
            </option>
          ))}
        </select>
      </label>

      {target && reportedUnit.trim() !== "" ? (
        needsFactor ? (
          <div className="flex flex-col gap-xs">
            <label className="flex flex-col gap-xs text-xs text-textSecondary">
              Omräkningsfaktor
              <input
                value={factorText}
                onChange={(e) => setFactorText(e.target.value)}
                inputMode="decimal"
                className={`${inputClass} w-40 tabular-nums`}
              />
            </label>
            <p className="text-xs text-textSecondary">
              Labbets enhet {reportedUnit.trim()} skiljer sig från analysens
              enhet {target.unit}. Värdet multipliceras med faktorn.
              {typeof factor === "number"
                ? ` Exempel: 100 ${reportedUnit.trim()} blir ${fmtNumber(convertPreview(100, factor))} ${target.unit}.`
                : ""}
            </p>
            {factor === "invalid" ? (
              <p className="text-xs text-danger">
                Ange faktorn som ett positivt tal.
              </p>
            ) : null}
          </div>
        ) : (
          <p className="text-xs text-textSecondary">
            Samma enhet som analysen. Värdet sparas som det är.
          </p>
        )
      ) : null}

      <ReasonInput
        value={reason}
        onChange={setReason}
        label="Varför stämmer kopplingen?"
      />
      {reason && reasonError ? (
        <p className="text-xs text-danger">{reasonError}</p>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}
      <div className="flex gap-sm">
        <button
          type="submit"
          disabled={!ready || busy}
          className="px-md py-sm rounded-md bg-brand text-white text-sm hover:opacity-90 disabled:opacity-50"
        >
          {busy ? "Sparar…" : "Spara kopplingen"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-md py-sm rounded-md border border-surfaceMuted text-sm"
        >
          Avbryt
        </button>
      </div>
    </form>
  );
}

export function RerunPanel({
  count,
  onDone,
  onUnauthorized,
}: {
  count: number;
  onDone: (r: RerunLabPortalenQuarantineResult) => void;
  onUnauthorized: () => void;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reasonError = resolutionReasonError(reason);

  async function submit() {
    if (reasonError || busy) return;
    setBusy(true);
    setError(null);
    try {
      onDone(await rerunLabPortalenQuarantine(reason));
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized();
        return;
      }
      setError(failText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      className="flex flex-col gap-sm max-w-2xl border border-surfaceMuted rounded-md p-md bg-surface"
    >
      <p className="text-sm">
        {/* TES-1620 — covers the no_open_order and no_patient rows too.
            Proposed copy, raised as an open question. */}
        {count} provsvar kan kopplas om. De kopplas på samma sätt som synken
        gör: till patientens öppna beställning, eller till en ny beställning som
        skapas från svaret. Läkaren granskar svaren innan patienten ser dem.
      </p>
      <ReasonInput
        value={reason}
        onChange={setReason}
        label="Varför körs kopplingen om?"
      />
      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}
      <div>
        <button
          type="submit"
          disabled={!!reasonError || busy}
          className="px-md py-sm rounded-md bg-brand text-white text-sm hover:opacity-90 disabled:opacity-50"
        >
          {busy ? "Kopplar…" : "Koppla om provsvaren"}
        </button>
      </div>
    </form>
  );
}

export function MappingsSection({
  mappings,
  canManage,
  onChanged,
  onUnauthorized,
}: {
  mappings: LabAnalyteMapping[];
  canManage: boolean;
  onChanged: (notice: string) => void;
  onUnauthorized: () => void;
}) {
  const [deactivating, setDeactivating] = useState<string | null>(null);
  return (
    <section
      aria-labelledby="lab-analyte-mappings"
      className="bg-surface border border-surfaceMuted rounded-md p-md flex flex-col gap-sm"
    >
      <h2 id="lab-analyte-mappings" className="font-medium">
        Analyskopplingar
      </h2>
      {mappings.length === 0 ? (
        <p className="text-sm text-textSecondary">Inga kopplingar ännu.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-textSecondary text-left">
                <th className="pr-md py-xs font-medium">Kod</th>
                <th className="pr-md py-xs font-medium">Namn</th>
                <th className="pr-md py-xs font-medium">Enhet</th>
                <th className="pr-md py-xs font-medium">Labb</th>
                <th className="pr-md py-xs font-medium">Analys</th>
                <th className="pr-md py-xs font-medium">Faktor</th>
                <th className="pr-md py-xs font-medium">Orsak</th>
                <th className="pr-md py-xs font-medium">Skapad</th>
                <th className="py-xs font-medium" />
              </tr>
            </thead>
            <tbody>
              {mappings.map((m) => (
                <MappingRow
                  key={m.id}
                  mapping={m}
                  canManage={canManage}
                  open={deactivating === m.id}
                  onToggle={() =>
                    setDeactivating((d) => (d === m.id ? null : m.id))
                  }
                  onDone={() => {
                    setDeactivating(null);
                    onChanged("Kopplingen är inaktiverad.");
                  }}
                  onUnauthorized={onUnauthorized}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function MappingRow({
  mapping: m,
  canManage,
  open,
  onToggle,
  onDone,
  onUnauthorized,
}: {
  mapping: LabAnalyteMapping;
  canManage: boolean;
  open: boolean;
  onToggle: () => void;
  onDone: () => void;
  onUnauthorized: () => void;
}) {
  return (
    <>
      <tr
        className={`border-t border-surfaceMuted align-top ${m.active ? "" : "text-textSecondary"}`}
      >
        <td className="pr-md py-xs font-mono text-xs">
          {m.testMethodCode ?? "–"}
        </td>
        <td className="pr-md py-xs">{m.anaName ?? "–"}</td>
        <td className="pr-md py-xs">{m.reportedUnit}</td>
        <td className="pr-md py-xs font-mono text-xs">{mappingScopeText(m)}</td>
        <td className="pr-md py-xs">
          {labAnalyteName(m.analyteKey)} ({m.targetUnit})
        </td>
        <td className="pr-md py-xs tabular-nums">
          {m.conversionFactor === null ? "–" : fmtNumber(m.conversionFactor)}
        </td>
        <td className="pr-md py-xs max-w-xs">{m.reason}</td>
        <td className="pr-md py-xs whitespace-nowrap">
          {fmtDate(m.createdAt)}
          {m.createdByName ? `, ${m.createdByName}` : ""}
        </td>
        <td className="py-xs whitespace-nowrap">
          {m.active ? (
            canManage ? (
              <button
                type="button"
                onClick={onToggle}
                aria-expanded={open}
                className="text-brand hover:underline"
              >
                Inaktivera
              </button>
            ) : (
              "Aktiv"
            )
          ) : (
            `Inaktiverad ${fmtDate(m.deactivatedAt)}`
          )}
        </td>
      </tr>
      {open ? (
        <tr>
          <td colSpan={9} className="py-sm">
            <DeactivateForm
              id={m.id}
              onDone={onDone}
              onUnauthorized={onUnauthorized}
            />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function DeactivateForm({
  id,
  onDone,
  onUnauthorized,
}: {
  id: string;
  onDone: () => void;
  onUnauthorized: () => void;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reasonError = resolutionReasonError(reason);

  async function submit() {
    if (reasonError || busy) return;
    setBusy(true);
    setError(null);
    try {
      await deactivateAnalyteMapping(id, reason);
      onDone();
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized();
        return;
      }
      setError(failText(e));
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      className="flex flex-col gap-sm max-w-2xl"
    >
      <p className="text-xs text-textSecondary">
        Nya provsvar läses inte längre med kopplingen. Sparade provsvar ändras
        inte.
      </p>
      <ReasonInput
        value={reason}
        onChange={setReason}
        label="Varför inaktiveras kopplingen?"
      />
      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : null}
      <div>
        <button
          type="submit"
          disabled={!!reasonError || busy}
          className="px-md py-sm rounded-md border border-surfaceMuted text-sm disabled:opacity-50"
        >
          {busy ? "Inaktiverar…" : "Inaktivera kopplingen"}
        </button>
      </div>
    </form>
  );
}
